import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  CustomCollapseCard,
  CustomImage,
  CustomMoneyInput,
  CustomForm,
  CustomFormItem,
  CustomInput,
  CustomNumberInput,
  CustomCheckbox,
  CustomButton,
  errorDialog,
  Text,
  useText,
} from '@src/components';
import { Collapse, Form } from 'antd';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { isGreaterMaximumLoadedAmount, moneyFormat } from '@src/utils/utils';
import CustomMultiSelect from '@src/components/CustomMultiSelect';
import {
  instructionDetailPriceUpdate,
  getDetailsPageList,
  getInstructionsDetailsPageList,
  setSelecteAutoUploadDetails,
  getTotalAutoLoadingParams,
  setIntegrationId,
  setResetDetailList,
  setIsAllSelectedRowKeys,
  setAutoLoadingNotIncludedCards,
  setSelectedRowKeys,
  resetAutoUploadFilterItems,
  setDetailList,
} from '@src/store/slice/autoUploadInstructionsSlice';
import { addressGetPagedListByUserQuery } from '@src/store/slice/addressSlice';
import { VirtualizedAndLazyScrollTable } from '@src/components/VirtualizedAndLazyScrollTable';
import iconSearchWhite from '@src/assets/icons/icon-white-search.svg';
import cardIcon from '@src/assets/icons/icon-kart.svg';
import '@src/styles/autoUploadInstruction/cardListInformation.scss';
import { debounce } from 'lodash/function';
import { setLoadingState } from '@src/store/slice/appSettingsSlice';
const { Panel } = Collapse;

const CardListInformation = () => {
  const isMobil = useMediaQuery({ query: '(max-width: 1199.98px)' });
  const dispatch = useDispatch();
  const history = useHistory();
  const [form] = Form.useForm();

  const { maximumLoadedAmount } = useSelector((state) => state?.orderLimits);
  const { addressListByUserQuery } = useSelector((state) => state?.address);
  const {
    detailList,
    detailTableProperty,
    totalAutoLoading,
    autoLoadingId,
    integrationId,
    isAllSelectedRowKeys,
    autoLoadingNotIncludedCards,
    selectedRowKeys,
  } = useSelector((state) => state?.autoUploadInstructions);
  const [selectedAddressListReset, setSelectedAddressListReset] = useState(false);

  const [lazyloadPageSize, setLazyloadPageSize] = useState(350);
  const [isBtnActive, setIsBtnActive] = useState(false);
  const [isGetAddresses, setIsGetAddresses] = useState(false);
  const [exceptSelectedCartCount, setExceptSelectedCartCount] = useState(0);

  const maximumLoadingLimitMessage = useText('maximumLoadingLimitPerCardIs');
  const amountPlaceholder = useText('amount3');
  const referenceNumberPlaceholder = useText('referenceNumber');
  const deliveryAddressPlaceholder = useText('deliveryAddress2');
  const referenceCodeTitle = useText('referenceCode3');
  const cardNumberTitle = useText('cardNumber4');
  const totalBalanceTitle = useText('totalBalanceToBeLoadedVatIncludedUpperCase');

  const columns = [
    {
      title: useText('nameUpperCase'),
      dataIndex: 'name',
      key: 'name',
      width: 100,
      sorter: true,
    },
    {
      title: useText('surnameUpperCase'),
      dataIndex: 'surname',
      key: 'surname',
      width: 100,
      sorter: true,
    },
  ];

  !isMobil &&
    columns.push(
      {
        title: referenceCodeTitle,
        dataIndex: 'refNo',
        key: 'refNo',
        width: 100,
        sorter: true,
      },
      {
        title: cardNumberTitle,
        dataIndex: 'cardNumber',
        key: 'cardNumber',
        width: 140,
        sorter: true,
      },
      {
        title: totalBalanceTitle,
        dataIndex: 'price',
        key: 'price',
        width: 85,
        align: 'right',
        render: (text, record) => {
          return (
            <CustomMoneyInput
              id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-price-money-input"
              data-testid="price"
              value={text}
              placeholder={amountPlaceholder}
              width={170}
              height={36}
              onBlur={(e) => addUpdatePrice(e.target.value, record)}
              addUpdatePrice={addUpdatePrice}
              record={record}
              onInput={(e) => {
                updateWithDebounce(e.target.value, record);
              }}
            />
          );
        },
      },
    );

  const sortRef = useRef({
    orderType: '',
    orderByValue: '',
  });

  const addUpdatePrice = async (value, record, request = false) => {
    const val = value?.replaceAll('.', '')?.replace(',', '.');
    const v = val === '' ? 0 : parseFloat(val);
    if (record?.price === v) return;

    if (
      isGreaterMaximumLoadedAmount({
        controlValue: maximumLoadedAmount,
        price: v,
      })
    ) {
      errorDialog({
        title: <Text t="error" />,
        message: maximumLoadingLimitMessage.replaceAll('{amount}', maximumLoadedAmount),
      });
      return;
    }
    let data = {
      autoLoadingId: totalAutoLoading?.id,
      price: v,
      cardNumbers: [record.cardNumber],
    };

    let newAutoUploadDetails = detailList?.map((item) => {
      if (item?.cardNumber === record?.cardNumber) {
        return { ...item, price: data.price };
      }
      return item;
    });
    await dispatch(setDetailList(newAutoUploadDetails));

    await dispatch(setLoadingState(false));
    request && (await dispatch(instructionDetailPriceUpdate(data)));
    await dispatch(setLoadingState(true));

    if (instructionDetailPriceUpdate.fulfilled.match) {
      if (autoLoadingId && autoLoadingId > 0) {
        await dispatch(getTotalAutoLoadingParams({ autoLoadingId: autoLoadingId }));
      }
    }
  };

  const updateWithDebounce = useCallback(
    debounce(async (value, record) => {
      await addUpdatePrice(value, record, true);
    }, 2000),
    [addUpdatePrice],
  );

  const loadInstructionsDetailsPageList = useCallback(
    async (pageNumber, pageSize) => {
      if (!pageSize) {
        pageSize = lazyloadPageSize;
      }
      setLazyloadPageSize(pageSize);
      await dispatch(
        getDetailsPageList({
          page: 1,
          pageSize: pageSize,
          showOnlyPriced: false,
          autoLoadingId: totalAutoLoading?.id,
          OrderType: sortRef.current.orderType,
          OrderByValue: sortRef.current.orderByValue,
        }),
      );
    },
    [dispatch],
  );

  const handleTableSortedChange = useCallback(
    async (pagination, filters, sorter, allTable) => {
      const { order, field } = sorter;
      if (!order) {
        sortRef.current = {};
      } else {
        sortRef.current = {
          orderType: order === 'ascend' ? 'asc' : 'desc',
          orderByValue: field,
        };
      }
      await loadInstructionsDetailsPageList({}, allTable?.currentDataSource.length || 30);
    },
    [loadInstructionsDetailsPageList],
  );

  const onGridSelectChange = (record, data) => {
    setExceptSelectedCartCount(
      detailTableProperty?.totalCount < 350
        ? detailTableProperty?.totalCount - record.length
        : lazyloadPageSize - record.length,
    );
    dispatch(setSelectedRowKeys(record));
    dispatch(setSelecteAutoUploadDetails(data));
  };

  useEffect(async () => {
    const params = new URLSearchParams(history.location.search);
    let integrationId = params.get('updateId') ?? 0;
    if (!integrationId) {
      integrationId = params.get('instructionId') ?? 0;
    }
    if (integrationId > 0) {
      const res = await dispatch(
        getDetailsPageList({
          page: 1,
          pageSize: 350,
          showOnlyPriced: false,
          integrationId: integrationId,
        }),
      );

      if (getDetailsPageList.fulfilled.match(res)) {
        const body = {
          autoLoadingId: 0,
          integrationId: integrationId,
        };

        const actionGetTotalParam = await dispatch(getTotalAutoLoadingParams(body));

        if (getTotalAutoLoadingParams.fulfilled.match(actionGetTotalParam)) {
          await dispatch(setIntegrationId(Number(integrationId)));
        }
      }
    } else {
      await dispatch(
        getDetailsPageList({
          page: 1,
          pageSize: 350,
          showOnlyPriced: false,
          autoLoadingId: totalAutoLoading?.id,
        }),
      );
    }
  }, [dispatch, history, integrationId]);

  useEffect(() => {
    (async () => {
      if (addressListByUserQuery.length === 0 && !isGetAddresses) {
        setIsGetAddresses(true);
        await dispatch(
          addressGetPagedListByUserQuery({
            pageNumber: 0,
            pageSize: 0,
          }),
        );
      }
    })();
  }, [addressListByUserQuery, dispatch, isGetAddresses]);

  useEffect(() => {
    return () => {
      dispatch(setResetDetailList([]));
    };
  }, []);

  useEffect(async () => {
    if (isAllSelectedRowKeys) {
      const selectedKeys = detailList
        .filter(
          (detail) =>
            !autoLoadingNotIncludedCards.some((card) => card.cardNumber === detail.cardNumber),
        )
        .map((b) => `list-${b?.cardNumber}`);
      dispatch(setSelectedRowKeys(selectedKeys));
      if (autoLoadingNotIncludedCards.length == 0) {
        setExceptSelectedCartCount(0);
      }
    }
  }, [detailList]);

  const submitFilter = async () => {
    let values = await form.validateFields();
    if (values.deliveryCode) {
      values.addressIdList = [values.deliveryCode];
    }
    delete values.deliveryAddress;
    delete values.deliveryCode;

    await dispatch(
      getInstructionsDetailsPageList({
        filter: values,
        body: { autoLoadingId: totalAutoLoading?.id },
        pageSize: lazyloadPageSize,
        pageNumber: 1,
      }),
    );
  };

  const clearForm = () => {
    setIsBtnActive(false);
    form.resetFields();
    setSelectedAddressListReset(true);
    dispatch(resetAutoUploadFilterItems());
    dispatch(
      getInstructionsDetailsPageList({
        filter: {},
        body: { autoLoadingId: totalAutoLoading?.id },
        pageSize: lazyloadPageSize,
        pageNumber: 1,
      }),
    );
  };

  const onValuesChange = async () => {
    const allValues = await form.getFieldsValue();
    if (
      (allValues?.cardNumber ||
        allValues?.addressIdList?.length ||
        allValues?.deliveryCode ||
        allValues?.name ||
        allValues?.refNo ||
        allValues?.showOnlyPriced ||
        allValues?.surname) &&
      (allValues?.cardNumber !== '' ||
        allValues?.addressIdList?.length ||
        allValues?.deliveryCode !== '' ||
        allValues?.name !== '' ||
        allValues?.refNo !== '' ||
        allValues?.showOnlyPriced ||
        allValues?.surname !== '')
    ) {
      setIsBtnActive(true);
      return;
    }
    setIsBtnActive(false);
  };

  const addressListForMultiSelect = useMemo(
    () =>
      addressListByUserQuery?.map((item) => {
        return {
          label: item.addressName,
          value: item.id,
        };
      }),
    [addressListByUserQuery],
  );

  const addressValueChange = async (values) => {
    form.setFieldsValue({ addressIdList: values.map((item) => ({ addressId: item.value })) });
    setSelectedAddressListReset(false);
    setTimeout(async () => {
      await onValuesChange();
    }, 200);
  };

  return (
    <CustomCollapseCard
      cardTitle={<Text t="userList" />}
      className="autoUploadInstruction-instruction-collapse"
    >
      <div className="user-list-filter-container">
        <Collapse
          defaultActiveKey={['1']}
          className="filter-collapse-card"
          ghost
          expandIconPosition="right"
        >
          <Panel
            collapsible="disabled"
            header={
              <div>
                <Text t="addFilter" />
              </div>
            }
            key="1"
            showArrow={false}
          >
            <div>
              <CustomForm
                name="filterForm"
                className="filter-form"
                initialValues={{
                  showOnlyPriced: false,
                }}
                onValuesChange={onValuesChange}
                autoComplete="off"
                layout={'horizontal'}
                form={form}
                id="loadBalanceFilterForm"
              >
                <div className="form-item">
                  <CustomFormItem name="name" className="name-form-item">
                    <CustomInput
                      id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-name-input"
                      placeholder={useText('name')}
                      height={36}
                    />
                  </CustomFormItem>

                  <CustomFormItem name="surname" className="surname-form-item">
                    <CustomInput
                      id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-surname-input"
                      placeholder={useText('surname')}
                      height={36}
                    />
                  </CustomFormItem>

                  <CustomFormItem name="cardNumber" className="card-number-form-item">
                    <CustomNumberInput
                      id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-cardNumber-numberInput"
                      autoComplete="off"
                      placeholder={useText('cardNumber2')}
                      height={36}
                    />
                  </CustomFormItem>

                  <CustomFormItem name="refNo" className="reference-code-form-item">
                    <CustomInput
                      id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-refNo-input"
                      autoComplete="off"
                      placeholder={referenceNumberPlaceholder}
                      height={36}
                    />
                  </CustomFormItem>
                  <CustomFormItem name="addressIdList" style={{ display: 'none' }}></CustomFormItem>
                  <CustomMultiSelect
                    source={addressListForMultiSelect}
                    reset={selectedAddressListReset}
                    onCustomMultiSelectChange={addressValueChange}
                    selectSomeItems={deliveryAddressPlaceholder}
                  />

                  <div className="form-footer">
                    <CustomFormItem
                      label={false}
                      name="showOnlyPriced"
                      className="footer-form-item"
                      valuePropName="checked"
                    >
                      <CustomCheckbox
                        id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-showOnlyPriced-checkbox"
                        className="only-amount-check"
                      >
                        <Text t="showThoseWithAmountEntered2" />
                      </CustomCheckbox>
                    </CustomFormItem>
                    <div className="action-buttons">
                      <CustomButton
                        id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-clear-btn"
                        className="clear-btn"
                        onClick={clearForm}
                      >
                        <Text t="clean" />
                      </CustomButton>
                      <CustomButton
                        id="ticketRestaurant-autoUploadInstructions-instructionInformations-cardListInformation-search-btn"
                        className="search-btn"
                        disabled={!isBtnActive}
                        onClick={submitFilter}
                      >
                        <CustomImage className="icon-search" src={iconSearchWhite} />{' '}
                        <Text t="search" />
                      </CustomButton>
                    </div>
                  </div>
                </div>
              </CustomForm>
            </div>
          </Panel>
        </Collapse>
      </div>

      {detailList.length > 0 && (
        <VirtualizedAndLazyScrollTable
          rowSelection={{
            preserveSelectedRowKeys: true,
            type: 'checkbox',
            selectedRowKeys,
            onChange: (selectedRowKeys, selectedRows) => {
              onGridSelectChange(selectedRowKeys, selectedRows);
              const deselectedRows = detailList.filter(
                (row) => !selectedRowKeys.includes(`list-${row?.cardNumber}`),
              );
              dispatch(setAutoLoadingNotIncludedCards(deselectedRows));
            },
            onSelectAll: async (selected) => {
              await dispatch(setIsAllSelectedRowKeys(selected));
            },
          }}
          className="autoUploadInstruction-instruction-collapse-table"
          pagination={false}
          dataSource={detailList}
          getDataFunc={loadInstructionsDetailsPageList}
          columnsData={columns}
          rowKey={(record) => `list-${record?.cardNumber}`}
          onChange={handleTableSortedChange}
        />
      )}
      {createPortal(
        <div className="autoUploadInstruction-instruction-collapse-footer">
          <div className="autoUploadInstruction-instruction-collapse-footer-container">
            <div className="autoUploadInstruction-instruction-collapse-footer-container-info-content">
              <CustomImage src={cardIcon} />
              <span>
                <Text t="total" />
                <strong>
                  {isAllSelectedRowKeys
                    ? detailTableProperty?.totalCount - exceptSelectedCartCount
                    : selectedRowKeys.length}
                </strong>{' '}
                <Text t="cardsSelected" />
              </span>
            </div>
            <div className="autoUploadInstruction-instruction-collapse-footer-container-total-amount-title">
              <span>
                <strong>
                  <Text t="totalBalanceToBeLoadedUpperCaseColon" />
                </strong>
                {moneyFormat(totalAutoLoading?.totalPrice, true)}
              </span>
            </div>
          </div>
        </div>,
        document.getElementById('custom-sticky-container') || document.createElement('div'),
      )}
    </CustomCollapseCard>
  );
};

export default CardListInformation;
